import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { usePublicConditionWallets } from "@/hooks/use-queries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search, TrendingUp } from "lucide-react";
import { useState, useMemo } from "react";
import { type MedicalCondition, type ConditionWallet, ConditionCard, WalletCard, formatCurrency } from "./shared";

export function ConditionsList() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  
  const { data: conditions, isLoading } = useQuery<MedicalCondition[]>({
    queryKey: ['/api/public/conditions'],
  });
  
  const { data: wallets } = usePublicConditionWallets();
  
  const walletCountByCondition = useMemo(() => {
    if (!wallets) return {};
    return wallets.reduce((acc: Record<string, number>, w: any) => {
      acc[w.conditionId] = (acc[w.conditionId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [wallets]);
  
  const filteredConditions = useMemo(() => {
    if (!conditions) return [];
    if (!searchQuery.trim()) return conditions;
    const query = searchQuery.toLowerCase();
    return conditions.filter(c => 
      c.name.toLowerCase().includes(query) || 
      c.description.toLowerCase().includes(query)
    );
  }, [conditions, searchQuery]);
  
  const featuredWallets = useMemo(() => {
    if (!wallets) return [];
    return wallets.filter((w: any) => w.isFeatured).slice(0, 3);
  }, [wallets]);
  
  const totalStats = useMemo(() => {
    if (!wallets) return { raised: 0, members: 0, contributors: 0, deliveries: 0 };
    return wallets.reduce((acc: any, w: any) => ({
      raised: acc.raised + w.totalRaised,
      members: acc.members + w.memberCount,
      contributors: acc.contributors + w.contributorCount,
      deliveries: acc.deliveries + w.servicesDelivered,
    }), { raised: 0, members: 0, contributors: 0, deliveries: 0 });
  }, [wallets]);
  
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-10">
      <div className="flex items-center gap-2 mb-4">
        <Link href="/">
          <Button variant="ghost" size="sm" data-testid="button-home-breadcrumb">
            {t('conditions.home')}
          </Button>
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-sm font-medium text-foreground">{t('conditions.community-funds-label')}</span>
      </div>
      
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-xl p-4 sm:p-8">
        <div className="max-w-2xl">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" data-testid="text-page-title">
            {t('conditions.heading')}
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            {t('conditions.subheading')}
          </p>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder={t('conditions.search-placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="input-search-conditions"
            />
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-4 text-center">
          <div className="text-xl sm:text-2xl font-bold text-primary">{formatCurrency(totalStats.raised)}</div>
          <div className="text-sm text-muted-foreground">{t('conditions.stat-total-raised')}</div>
        </Card>
        <Card className="p-3 sm:p-4 text-center">
          <div className="text-xl sm:text-2xl font-bold text-primary">{totalStats.members}</div>
          <div className="text-sm text-muted-foreground">{t('conditions.stat-members-helped')}</div>
        </Card>
        <Card className="p-3 sm:p-4 text-center">
          <div className="text-xl sm:text-2xl font-bold text-primary">{totalStats.contributors}</div>
          <div className="text-sm text-muted-foreground">{t('conditions.stat-contributors')}</div>
        </Card>
        <Card className="p-3 sm:p-4 text-center">
          <div className="text-xl sm:text-2xl font-bold text-primary">{totalStats.deliveries}</div>
          <div className="text-sm text-muted-foreground">{t('conditions.stat-payments-funded')}</div>
        </Card>
      </div>
      
      {featuredWallets.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">{t('conditions.featured-heading')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {featuredWallets.map((wallet: any) => (
              <WalletCard key={wallet.id} wallet={wallet} />
            ))}
          </div>
        </section>
      )}
      
      <section>
        <h2 className="text-xl font-semibold mb-4">{t('conditions.browse-heading')}</h2>
        {filteredConditions.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">
              {searchQuery
                ? t('conditions.no-match', { query: searchQuery })
                : t('conditions.no-conditions')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredConditions.map(condition => (
              <ConditionCard 
                key={condition.id} 
                condition={condition} 
                walletCount={walletCountByCondition[condition.id] || 0}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
